import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { DEG, TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { GlyphParticlesValues } from './params';

/**
 * Glyph particles: the same flow-field motion, but each particle *is* a character.
 *
 * Characters are drawn with the canvas text API rather than from the loaded font's
 * outlines. That is a deliberate trade: the emitted characters are usually not the
 * text's own glyphs (an alphabet, digits, symbols), and going through the outline path
 * would require the loaded font to contain them — which for a display face it often
 * does not.
 */
export interface GlyphParticlesState {
  buffer: OffscreenCanvas | HTMLCanvasElement;
  bctx: Ctx2D;
  x: Float32Array;
  y: Float32Array;
  size: Float32Array;
  tint: Float32Array;
  charIndex: Uint16Array;
  count: number;
  chars: string[];
  noise: NoiseFunction2D;
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!?§$%&/()=*+#~<>|';

function values(ctx: TechniqueContext): GlyphParticlesValues {
  return ctx.values as unknown as GlyphParticlesValues;
}

function charsetFor(v: GlyphParticlesValues, ctx: TechniqueContext): string[] {
  switch (v.source) {
    case 'alphabet':
      return [...ALPHABET];
    case 'digits':
      return [...DIGITS];
    case 'symbols':
      return [...SYMBOLS];
    case 'custom':
      return [...(v.customChars || '◆')];
    default: {
      const own = ctx.field.glyphs.filter((g) => !g.isWhitespace).map((g) => g.char);
      return own.length ? own : [...ALPHABET];
    }
  }
}

export function init(ctx: TechniqueContext): GlyphParticlesState {
  const v = values(ctx);
  const count = Math.max(1, v.count);
  const buffer = createCanvas(ctx.width, ctx.height);
  const bctx = get2d(buffer);
  bctx.textAlign = 'center';
  bctx.textBaseline = 'middle';

  const state: GlyphParticlesState = {
    buffer,
    bctx,
    x: new Float32Array(count),
    y: new Float32Array(count),
    size: new Float32Array(count),
    tint: new Float32Array(count),
    charIndex: new Uint16Array(count),
    count,
    chars: charsetFor(v, ctx),
    noise: createNoise2D(() => ctx.rng.next()),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.steps,
  };

  const outline = v.origin === 'outline' ? ctx.field.sampleOutline(Math.min(4000, count)) : [];
  const interior = v.origin === 'inside' ? ctx.field.samplePoints(Math.min(4000, count), 'poisson', ctx.rng) : new Float32Array(0);

  for (let i = 0; i < count; i++) {
    if (outline.length) {
      const s = outline[ctx.rng.int(0, outline.length - 1)];
      const push = 2 + ctx.rng.next() * 10;
      state.x[i] = s.x + s.nx * push;
      state.y[i] = s.y + s.ny * push;
    } else if (interior.length >= 2) {
      const k = ctx.rng.int(0, interior.length / 2 - 1) * 2;
      state.x[i] = interior[k];
      state.y[i] = interior[k + 1];
    } else if (v.origin === 'edges') {
      const side = ctx.rng.int(0, 3);
      const t = ctx.rng.next();
      if (side === 0) {
        state.x[i] = t * ctx.width;
        state.y[i] = 0;
      } else if (side === 1) {
        state.x[i] = ctx.width;
        state.y[i] = t * ctx.height;
      } else if (side === 2) {
        state.x[i] = t * ctx.width;
        state.y[i] = ctx.height;
      } else {
        state.x[i] = 0;
        state.y[i] = t * ctx.height;
      }
    } else {
      state.x[i] = ctx.rng.next() * ctx.width;
      state.y[i] = ctx.rng.next() * ctx.height;
    }

    state.size[i] = ctx.rng.range(v.size[0], v.size[1]);
    state.tint[i] = ctx.rng.next();
    state.charIndex[i] = ctx.rng.int(0, state.chars.length - 1);
  }

  return state;
}

function paint(
  state: GlyphParticlesState,
  v: GlyphParticlesValues,
  ctx: TechniqueContext,
  i: number,
  angle: number,
  lifeT: number,
): void {
  const bctx = state.bctx;
  let t: number;
  switch (v.colorMode) {
    case 'distance':
      t = clamp01(Math.abs(ctx.field.distance(state.x[i], state.y[i])) / Math.max(1, ctx.field.maxOutside));
      break;
    case 'life':
      t = lifeT;
      break;
    case 'random':
      t = state.tint[i];
      break;
    default:
      t = 0;
  }
  bctx.fillStyle = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
  bctx.font = `${state.size[i].toFixed(1)}px ui-sans-serif, system-ui, sans-serif`;

  const rotation = (v.rotateToMotion ? angle : 0) + (state.tint[i] * 2 - 1) * v.rotationJitter * DEG;
  if (rotation !== 0) {
    bctx.save();
    bctx.translate(state.x[i], state.y[i]);
    bctx.rotate(rotation);
    bctx.fillText(state.chars[state.charIndex[i]], 0, 0);
    bctx.restore();
  } else {
    bctx.fillText(state.chars[state.charIndex[i]], state.x[i], state.y[i]);
  }
}

export function step(state: GlyphParticlesState, _dt: number, ctx: TechniqueContext): GlyphParticlesState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);
  const perFrame = Math.max(1, Math.round(3 * clamp01(ctx.quality)));

  for (let frame = 0; frame < perFrame && state.stepsDone < state.targetSteps; frame++) {
    const lifeT = state.stepsDone / Math.max(1, state.targetSteps - 1);

    for (let i = 0; i < state.count; i++) {
      const x = state.x[i];
      const y = state.y[i];

      let angle = state.noise(x * v.noiseScale, y * v.noiseScale) * TAU;
      const signed = ctx.field.distance(x, y);
      const falloff = 1 - clamp01(Math.abs(signed) / Math.max(1, v.textRadius));
      if (falloff > 0 && v.textRepel !== 0) {
        const [gx, gy] = ctx.field.gradient(x, y);
        if (gx !== 0 || gy !== 0) {
          const target = v.textRepel > 0 ? Math.atan2(gy, gx) : Math.atan2(-gy, -gx);
          const delta = ((target - angle + Math.PI * 3) % TAU) - Math.PI;
          angle += delta * clamp01(falloff * Math.abs(v.textRepel));
        }
      }

      let vx = Math.cos(angle) * v.stepSize;
      let vy = Math.sin(angle) * v.stepSize + v.gravity * v.stepSize;
      const len = Math.hypot(vx, vy) || 1;
      vx = (vx / len) * v.stepSize;
      vy = (vy / len) * v.stepSize;

      state.x[i] = x + vx;
      state.y[i] = y + vy;

      // Wrap so the field never empties out.
      if (state.x[i] < -40) state.x[i] += ctx.width + 80;
      else if (state.x[i] > ctx.width + 40) state.x[i] -= ctx.width + 80;
      if (state.y[i] < -40) state.y[i] += ctx.height + 80;
      else if (state.y[i] > ctx.height + 40) state.y[i] -= ctx.height + 80;

      const isLast = state.stepsDone === state.targetSteps - 1;
      if (v.trail ? state.stepsDone % v.trailEvery === 0 : isLast) {
        paint(state, v, ctx, i, Math.atan2(vy, vx), lifeT);
      }
    }

    state.stepsDone++;
  }

  return state;
}

export function isComplete(state: GlyphParticlesState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: GlyphParticlesState, target: RenderTarget): void {
  target.ctx.drawImage(state.buffer as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: GlyphParticlesState): number {
  return state.count;
}
